import { useEffect, useState, useMemo, useCallback } from 'react';
import Slider, { Settings } from 'react-slick';
import { api } from '../api/api';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface NewsItem {
  id: number;
  title: string;
  content: string;
  created_at?: string;
}

const sanitizeHTML = (html: string) => {
  if (typeof window === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const scripts = doc.querySelectorAll('script, iframe, object, embed');
  scripts.forEach(s => s.remove());
  return doc.body.innerHTML;
};

export default function NewsCarousel() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchNews = async () => {
      try {
        const data = await api.getNews();
        if (isMounted) setNews(data || []);
      } catch (err) {
        console.error('Помилка завантаження новин:', err);
      }
    };
    fetchNews();
    return () => { isMounted = false; };
  }, []);

  const extractImage = useCallback((html: string) => {
    if (typeof document === 'undefined') return null;
    const div = document.createElement('div');
    div.innerHTML = html;
    const img = div.querySelector('img');
    return img ? img.src : null;
  }, []);

  const extractText = useCallback((html: string, length: number = 100) => {
    if (typeof document === 'undefined') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  }, []);

  const settings: Settings = useMemo(() => ({
    dots: true,
    infinite: news.length > 1,
    speed: 600,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 5000,
    focusOnSelect: true,
    dotsClass: "slick-dots custom-dots",
    nextArrow: <Arrow direction="right" />,
    prevArrow: <Arrow direction="left" />,
    responsive: [
      { breakpoint: 1150, settings: { slidesToShow: 2, arrows: false } },
      { breakpoint: 768, settings: { slidesToShow: 1, arrows: false, centerMode: true, centerPadding: '40px' } },
      { breakpoint: 480, settings: { slidesToShow: 1, arrows: false, centerMode: true, centerPadding: '20px' } }
    ]
  }), [news.length]);

  return (
    <div className="news-wrapper">
      <style>{`
        .news-wrapper { 
          --mint: #1abc9c;
          --mint-dark: #16a085;
          --text-main: #1e2d2a;
          --border-strong: #c2ded7;
          background: #f4f7f6; 
          padding: clamp(50px, 10vw, 100px) 0 120px 0; 
          overflow: hidden; 
        }
        .news-section { max-width: 1300px; margin: 0 auto; padding: 0 40px; position: relative; }
        .news-title { text-align: center; margin-bottom: 50px; font-weight: 800; color: var(--text-main); font-family: 'Montserrat', sans-serif; font-size: clamp(1.8rem, 5vw, 2.8rem); }
        .news-title span { color: var(--mint); }

        .news-card-container { padding: 20px 15px; outline: none; }
        .news-card {
          border-radius: 24px; overflow: hidden; background: #ffffff; cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); height: 500px;
          display: flex !important; flex-direction: column; 
          border: 2px solid var(--border-strong);
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }

        .slick-center .news-card { border-color: var(--mint); transform: scale(1.03); box-shadow: 0 15px 35px rgba(26, 188, 156, 0.2); }
        .news-card:hover { transform: translateY(-10px); border-color: var(--mint); box-shadow: 0 20px 40px rgba(26, 188, 156, 0.15); }

        .image-container { height: 220px; overflow: hidden; background: #eaeeed; display: flex; align-items: center; justify-content: center; border-bottom: 1px solid var(--border-strong); }
        .news-image { width: 100%; height: 100%; object-fit: cover; }
        .card-body { padding: 25px; flex-grow: 1; display: flex; flex-direction: column; }

        /* DOTS */
        .custom-dots { bottom: -60px !important; display: flex !important; justify-content: center; list-style: none; padding: 0; width: 100%; }
        .custom-dots li { margin: 0 6px; display: inline-flex; align-items: center; }
        .custom-dots li button { width: 10px; height: 10px; padding: 0; background: #bdc3c7; border: none; border-radius: 50%; transition: 0.3s; cursor: pointer; }
        .custom-dots li.slick-active button { background: var(--mint); width: 30px; border-radius: 10px; }

        /* MODAL - ЧИСТА СТАТТЯ */
        .modal-overlay { 
          position: fixed; inset: 0; background: rgba(0,0,0,0.7); 
          display: flex; align-items: center; justify-content: center; 
          z-index: 10000; backdrop-filter: blur(8px); padding: 20px;
          animation: fadeIn 0.3s ease;
        }
        .modal-content { 
          background: #fff; max-width: 680px; width: 100%; max-height: 85vh; 
          border-radius: 30px; padding: 0; overflow-y: auto; position: relative; 
          box-shadow: 0 25px 60px rgba(0,0,0,0.3);
          animation: slideUp 0.4s ease-out;
          border: 2px solid var(--border-strong);
        }
        .modal-inner-body { padding: clamp(25px, 5vw, 50px); }
        .modal-close-btn {
          position: sticky; top: 15px; left: calc(100% - 55px);
          border: none; background: #f0f3f2; width: 40px; height: 40px; 
          border-radius: 50%; cursor: pointer; z-index: 11; font-size: 18px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1); transition: 0.2s;
        }
        .modal-close-btn:hover { background: #e0e6e5; transform: rotate(90deg); }

        .modal-date { color: var(--mint); font-weight: 700; font-size: 14px; margin-bottom: 15px; display: block; }
        .modal-title { font-size: clamp(20px, 4vw, 28px); color: var(--text-main); line-height: 1.3; margin-bottom: 25px; font-weight: 800; }
        
        /* Стилізація контенту всередині модалки */
        .modal-text { font-size: 16px; line-height: 1.8; color: #445552; }
        .modal-text img { 
          max-width: 100%; height: auto !important; border-radius: 20px; 
          margin: 25px auto; display: block; 
          box-shadow: 0 10px 25px rgba(0,0,0,0.1); 
          border: 1px solid var(--border-strong);
        }
        .modal-text p { margin-bottom: 1.5rem; }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }

        @media (max-width: 768px) {
          .modal-inner-body { padding: 30px 20px; }
        }
      `}</style>

      <div className="news-section">
        <h2 className="news-title">Свіжі <span>події</span> клініки</h2>

        {news.length === 0 ? (
          <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Завантаження новин...</p></div>
        ) : (
          <Slider {...settings}>
            {news.map(n => {
              const imageSrc = extractImage(n.content);
              return (
                <div key={n.id} className="news-card-container">
                  <div className="news-card" onClick={() => setSelectedNews(n)}>
                    <div className="image-container">
                      {imageSrc ? <img src={imageSrc} alt="" className="news-image" /> : <span style={{color: '#95a5a6', fontSize: '13px'}}>НОВИНА</span>}
                    </div>
                    <div className="card-body">
                      <span style={{ color: '#1abc9c', fontSize: '12px', fontWeight: '800', marginBottom: '8px', display: 'block' }}>
                        {n.created_at ? new Date(n.created_at).toLocaleDateString('uk-UA') : 'ПОДІЯ'}
                      </span>
                      <h3 style={{ fontSize: '18px', color: '#1e2d2a', marginBottom: '12px', fontWeight: '700', lineHeight: '1.4' }}>{n.title}</h3>
                      <p style={{ fontSize: '14px', color: '#53615f', lineHeight: '1.6' }}>{extractText(n.content, 85)}</p>
                      <div style={{ marginTop: 'auto', color: '#1abc9c', fontWeight: '800', fontSize: '12px' }}>ДЕТАЛЬНІШЕ →</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        )}

        {selectedNews && (
          <div className="modal-overlay" onClick={() => setSelectedNews(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-inner-body">
                <button className="modal-close-btn" onClick={() => setSelectedNews(null)}>✕</button>
                <span className="modal-date">
                  {selectedNews.created_at ? new Date(selectedNews.created_at).toLocaleDateString('uk-UA') : 'Новина клініки'}
                </span>
                <h2 className="modal-title">{selectedNews.title}</h2>
                <div 
                  className="modal-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(selectedNews.content) }} 
                />
                <button 
                  onClick={() => setSelectedNews(null)}
                  style={{ 
                    marginTop: '30px', width: '100%', padding: '16px', 
                    borderRadius: '15px', border: 'none', background: '#1abc9c', 
                    color: '#fff', fontWeight: '700', cursor: 'pointer', transition: '0.3s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#16a085'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#1abc9c'}
                >
                  Зрозуміло
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Arrow({ onClick, direction }: any) {
  const isLeft = direction === 'left';
  return (
    <div onClick={onClick} className="nav-arrow" style={{
      position: 'absolute', top: '50%', [isLeft ? 'left' : 'right']: '-60px',
      transform: 'translateY(-50%)', zIndex: 10, cursor: 'pointer',
      width: '50px', height: '50px', background: '#1abc9c',
      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 8px 20px rgba(26, 188, 156, 0.25)', transition: '0.3s'
    }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
        <path d={isLeft ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <style>{`
        .nav-arrow:hover { background: #16a085; transform: translateY(-50%) scale(1.1); }
        @media (max-width: 1250px) { .nav-arrow { display: none !important; } }
      `}</style>
    </div>
  );
}